/**
 * Tickets API client (A4 工单收口)
 *
 * Stream 2 Phase 2-3b / Plan §10 Stream 2 / Design §5.2 A4 工单收口.
 *
 * 包装 axios apiClient 调用 admin-service /tickets 端点；返回类型与 admin-service
 * ServiceResponse 形状对齐。写动作通过 client.ts 自动注入 Idempotency-Key。
 *
 * V1 范围：手动导入 + 列表 + 详情 + 派单 + 状态推进 + 上下文补充。
 * 飞书/企微自动同步、回写外部工单待 OPEN-18 关闭后实现。
 */
import { apiClient } from './client';

export type TicketSource = 'feishu' | 'wechat_work' | 'manual';
export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type TicketSyncStatus = 'synced' | 'pending' | 'out_of_sync' | 'manual';

export interface TicketListItem {
  id: string;
  externalTicketId: string;
  source: TicketSource;
  sourceUrl: string | null;
  title: string;
  reporterName: string | null;
  relatedUserId: string | null;
  relatedWorkId: string | null;
  relatedChapterId: string | null;
  relatedOrderId: string | null;
  status: TicketStatus;
  syncStatus: TicketSyncStatus;
  assigneeId: string | null;
  importedBy: string;
  importedAt: string;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketDetail extends TicketListItem {
  description: string | null;
  reporterContact: string | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  lastSyncedAt: string | null;
}

export interface ListTicketsResponse {
  items: TicketListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListTicketsFilter {
  status?: TicketStatus;
  source?: TicketSource;
  assigneeId?: string;
  relatedUserId?: string;
  syncStatus?: TicketSyncStatus;
  fromDate?: string; // ISO
  toDate?: string;   // ISO
  page?: number;
  pageSize?: number;
}

export interface ImportTicketInput {
  source: TicketSource;
  externalTicketId: string;
  sourceUrl?: string;
  title: string;
  description?: string;
  reporterName?: string;
  reporterContact?: string;
  relatedUserId?: string;
  relatedWorkId?: string;
  relatedChapterId?: string;
  relatedOrderId?: string;
}

export interface AssignTicketInput {
  assigneeId: string;
}

export interface UpdateTicketStatusInput {
  status: TicketStatus;
  resolutionNote?: string;
}

export interface LinkTicketContextInput {
  relatedUserId?: string;
  relatedWorkId?: string;
  relatedChapterId?: string;
  relatedOrderId?: string;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/** GET /tickets — 工单列表查询（分页 + 多字段过滤）。 */
export async function listTickets(
  filter: ListTicketsFilter = {},
): Promise<ListTicketsResponse> {
  const params: Record<string, string | number> = {};
  if (filter.status) params.status = filter.status;
  if (filter.source) params.source = filter.source;
  if (filter.assigneeId) params.assigneeId = filter.assigneeId;
  if (filter.relatedUserId) params.relatedUserId = filter.relatedUserId;
  if (filter.syncStatus) params.syncStatus = filter.syncStatus;
  if (filter.fromDate) params.fromDate = filter.fromDate;
  if (filter.toDate) params.toDate = filter.toDate;
  if (filter.page != null) params.page = filter.page;
  if (filter.pageSize != null) params.pageSize = filter.pageSize;

  const resp = await apiClient.get<ServiceResponse<ListTicketsResponse>>('/tickets', {
    params,
  });
  return resp.data.data;
}

/** GET /tickets/:id — 工单详情（含 description / resolutionNote 等扩展字段）。 */
export async function getTicket(id: string): Promise<TicketDetail> {
  const resp = await apiClient.get<ServiceResponse<TicketDetail>>(
    `/tickets/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

/** POST /tickets/import — 手动导入工单（V1 唯一导入方式）。 */
export async function importTicket(input: ImportTicketInput): Promise<void> {
  await apiClient.post<ServiceResponse<null>>('/tickets/import', input);
}

/** PATCH /tickets/:id/assign — 派单（指定处理人）。 */
export async function assignTicket(id: string, input: AssignTicketInput): Promise<void> {
  await apiClient.patch<ServiceResponse<null>>(
    `/tickets/${encodeURIComponent(id)}/assign`,
    input,
  );
}

/** PATCH /tickets/:id/status — 推进工单状态；resolved/closed 必填 resolutionNote。 */
export async function updateTicketStatus(
  id: string,
  input: UpdateTicketStatusInput,
): Promise<void> {
  await apiClient.patch<ServiceResponse<null>>(
    `/tickets/${encodeURIComponent(id)}/status`,
    input,
  );
}

/** PATCH /tickets/:id/link-context — 补充关联上下文（至少一字段非空）。 */
export async function linkTicketContext(
  id: string,
  input: LinkTicketContextInput,
): Promise<void> {
  await apiClient.patch<ServiceResponse<null>>(
    `/tickets/${encodeURIComponent(id)}/link-context`,
    input,
  );
}
